import { Link } from "react-router-dom";
import { Mail, Phone, UserRound } from "lucide-react";
import { useSeo } from "../lib/seo";
import { IMAGES, PHONE, PHONE_HREF, EMAIL } from "../lib/constants";
import { PageHero } from "../components/Common";
import { PageFade, Reveal } from "../components/Motion";

const PROCESS = ["Identify the opportunity", "Visit the property", "Gather information", "Evaluate the deal", "Communicate with the relevant parties", "Help move the transaction forward"];

export default function About() {
  useSeo("About Sido Holdings & Abdirahman Sido", "Sido Holdings is operated by Abdirahman Sido, a licensed Oregon Realtor making the commercial real estate acquisition process easier for investors.");
  return (
    <PageFade>
      <PageHero eyebrow="About Sido Holdings" title="Built to Make Real Estate Easier." image={IMAGES.landscape} />
      <section className="bg-paper py-20 sm:py-28">
        <div className="container-x grid gap-16 lg:grid-cols-[1fr_1.3fr]">
          <Reveal>
            <div className="relative aspect-[4/5] bg-navy grid place-items-center overflow-hidden grain" data-testid="founder-portrait-placeholder">
              <div className="text-center text-paper/50">
                <UserRound className="mx-auto h-20 w-20" strokeWidth={1} />
                <p className="mt-4 font-mono text-[11px] tracking-[0.2em]">FOUNDER PORTRAIT</p>
              </div>
              <div className="absolute bottom-0 inset-x-0 bg-navy/90 p-6 text-paper" data-testid="founder-card">
                <p className="font-serif text-3xl">Abdirahman Sido</p>
                <p className="mt-1 text-sm text-paper/70">Founder & Operator</p>
                <p className="text-sm text-gold">Licensed Oregon Realtor</p>
              </div>
            </div>
          </Reveal>
          <div className="space-y-6 text-lg leading-relaxed text-ink/80">
            <p className="font-serif text-3xl sm:text-4xl text-navy leading-snug">Sido Holdings is operated by Abdirahman Sido, a licensed Oregon Realtor with a passion for making the real-estate investment process easier for investors.</p>
            <p>Abdirahman understands that many investors have the capital and desire to acquire real estate but don't have the time to spend every day searching through listings, contacting owners, evaluating properties, visiting sites, researching opportunities, negotiating, and coordinating transactions.</p>
            <p>Sido Holdings exists to solve that problem. The company acts as a local acquisition partner for investors who provide their investment criteria, then searches for opportunities throughout Oregon that may fit those requirements.</p>
            <p>Investors aren't simply submitting a form into a faceless database. They are connecting with a local real-estate professional who will actively search for opportunities on their behalf.</p>
            <p className="border-l-2 border-gold pl-6 font-serif text-3xl text-navy" data-testid="about-legwork-quote">“Our job is to do the local legwork.”</p>
          </div>
        </div>
      </section>
      <section className="bg-navy text-paper py-20 sm:py-28 grain relative">
        <div className="container-x">
          <p className="eyebrow">A Hands-On Philosophy</p>
          <ol className="mt-10 grid gap-px bg-paper/10 sm:grid-cols-2 lg:grid-cols-3">
            {PROCESS.map((s, i) => (
              <Reveal key={s} delay={i * 0.05} className="bg-navy p-8">
                <span className="font-mono text-sm text-gold">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-4 font-serif text-2xl">{s}</p>
              </Reveal>
            ))}
          </ol>
          <div className="mt-14 flex flex-col sm:flex-row gap-3">
            <Link to="/investors#criteria" className="btn-gold" data-testid="about-cta-criteria">Tell Us What You're Looking For</Link>
            <a href={PHONE_HREF} className="btn-ghost text-paper hover:bg-paper hover:text-navy" data-testid="about-phone"><Phone className="h-4 w-4" />{PHONE}</a>
            <a href={`mailto:${EMAIL}`} className="btn-ghost text-paper hover:bg-paper hover:text-navy" data-testid="about-email"><Mail className="h-4 w-4" />{EMAIL}</a>
          </div>
        </div>
      </section>
    </PageFade>
  );
}
